import { afterPagePaint } from './after-page-paint.js';

// Keep silent autoplay, but let the first screen finish loading and paint first.
export function attachHeroVideo(video, update = () => {}, environment = window) {
  const document = environment.document;
  const preference = environment.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = environment.navigator?.connection;
  let ready = false;
  let sourceAttached = false;
  let disposed = false;
  let pending = false;
  let playing = false;
  let blocked = false;
  let attempt = 0;

  const notify = () => {
    if (!disposed) update({ playing, blocked });
  };
  const prepare = () => {
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
  };
  const pause = () => {
    attempt += 1;
    pending = false;
    playing = false;
    video.pause();
    notify();
  };
  const play = () => {
    if (disposed || !ready || document.hidden || preference?.matches || connection?.saveData || pending) return;
    prepare();
    if (!sourceAttached) {
      const source = video.dataset.mobileSrc && environment.matchMedia?.('(max-width: 767px)').matches
        ? video.dataset.mobileSrc : video.dataset.src;
      if (!source) return;
      sourceAttached = true;
      video.src = source;
      video.load();
    }
    if (!video.paused && !video.ended) return;
    const currentAttempt = ++attempt;
    pending = true;
    const failed = () => {
      if (disposed || currentAttempt !== attempt) return;
      pending = false;
      playing = false;
      blocked = true;
      notify();
    };
    try {
      Promise.resolve(video.play()).then(() => {
        if (disposed || currentAttempt !== attempt) return;
        pending = false;
        playing = true;
        blocked = false;
        notify();
      }, failed);
    } catch { failed(); }
  };
  const sync = () => {
    if (document.hidden || preference?.matches || connection?.saveData) pause();
    else play();
  };
  const onPlaying = () => { playing = true; blocked = false; notify(); };
  const onPause = () => { playing = false; notify(); };
  const onError = () => { playing = false; blocked = true; notify(); };
  const onEnded = () => { video.currentTime = 0; play(); };

  // Attach recovery listeners before loading. A failed first play must not
  // prevent loadeddata/canplay or a restored tab from trying again.
  video.addEventListener('loadeddata', sync);
  video.addEventListener('canplay', sync);
  video.addEventListener('playing', onPlaying);
  video.addEventListener('pause', onPause);
  video.addEventListener('error', onError);
  video.addEventListener('ended', onEnded);
  document.addEventListener('visibilitychange', sync);
  document.addEventListener('pointerdown', play);
  document.addEventListener('keydown', play);
  environment.addEventListener('pageshow', sync);
  preference?.addEventListener('change', sync);
  connection?.addEventListener?.('change', sync);

  // The responsive poster stays visible while the source is deferred. Hidden
  // tabs, reduced-motion and data-saving visitors do not download the video.
  prepare();
  const cancelStart = afterPagePaint(() => { ready = true; sync(); }, environment);

  return {
    cleanup() {
      disposed = true;
      cancelStart();
      attempt += 1;
      video.removeEventListener('loadeddata', sync);
      video.removeEventListener('canplay', sync);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('error', onError);
      video.removeEventListener('ended', onEnded);
      document.removeEventListener('visibilitychange', sync);
      document.removeEventListener('pointerdown', play);
      document.removeEventListener('keydown', play);
      environment.removeEventListener('pageshow', sync);
      preference?.removeEventListener('change', sync);
      connection?.removeEventListener?.('change', sync);
      video.pause();
    },
  };
}
